import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FileText, Trash2, Upload, X } from 'lucide-react';

const DocumentUploadWidget = ({ onUpload }) => {
    const [documents, setDocuments] = useState([]);
    const [title, setTitle] = useState('');
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            setFile(droppedFiles[0]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleAddDocument = () => {
        if (!title.trim()) {
            alert('Please enter a document title.');
            return;
        }
        if (!file) {
            alert('Please select a file.');
            return;
        }

        const newDoc = {
            id: Date.now(),
            title: title,
            fileName: file.name,
            fileSize: (file.size / 1024).toFixed(2) + ' KB',
        };

        setDocuments([...documents, newDoc]);

        if (onUpload) {
            onUpload(newDoc);
        }

        setTitle('');
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveDocument = (id) => {
        setDocuments(documents.filter((doc) => doc.id !== id));
    };

    const handleCancel = () => {
        setTitle('');
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="w-full max-w-2xl mx-auto p-6 font-sans">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden p-8">
                <h2 className="text-xl font-bold text-slate-800 mb-6">Multi-Document Upload</h2>

                {/* Input Form */}
                <div className="space-y-6">

                    {/* Title Input */}
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Document Title
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Project Requirements"
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all text-sm"
                        />
                    </div>

                    {/* Drag & Drop Area */}
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Upload File
                        </label>
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-all
                ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-slate-300 bg-slate-50'}
                ${file ? 'bg-teal-50 border-teal-500' : ''}
              `}
                        >
                            {!file ? (
                                <div className="flex flex-col items-center gap-3">
                                    <div className="p-3 bg-white rounded-full shadow-sm">
                                        <Upload className="text-teal-600" size={24} />
                                    </div>
                                    <p className="text-slate-600 text-sm">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-slate-400 text-xs">SVG, PNG, JPG or PDF (max. 10MB)</p>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        id="file-upload"
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="mt-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
                                    >
                                        Browse Files
                                    </label>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-teal-200">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-teal-100 rounded-lg">
                                            <FileText className="text-teal-600" size={20} />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">{file.name}</p>
                                            <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setFile(null)}
                                        className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded transition-colors"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddDocument}
                            className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm rounded-lg shadow-lg shadow-slate-900/20 transition-all active:scale-95"
                        >
                            Add Document
                        </button>
                    </div>

                </div>

                {/* Document List */}
                {documents.length > 0 && (
                    <div className="mt-8 border-t border-slate-100 pt-6">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">
                            Attached Documents ({documents.length})
                        </h3>
                        <div className="space-y-3">
                            {documents.map((doc) => (
                                <div
                                    key={doc.id}
                                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 group hover:border-teal-200 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="p-2 bg-white rounded-lg shadow-sm text-slate-400 group-hover:text-teal-500 transition-colors">
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800">{doc.title}</p>
                                            <p className="text-xs text-slate-500">{doc.fileName} • {doc.fileSize}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveDocument(doc.id)}
                                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                        title="Remove Document"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

DocumentUploadWidget.propTypes = {
    onUpload: PropTypes.func,
};

export default DocumentUploadWidget;
